import { proxy, snapshot, useSnapshot, subscribe } from "valtio";
import { Plan } from "@/types/plan.type";

const MAX_RECENT_PRODUCTS = 5;
const STORAGE_KEY = "recent-products-storage";

const loadPersistedRecentProducts = (): Plan[] => {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    return item ? JSON.parse(item) : [];
  } catch (error) {
    console.warn(`Error loading '${STORAGE_KEY}' from localStorage:`, error);
    return [];
  }
};

interface RecentProductsStoreState {
  recentProducts: Plan[];
  addRecentProduct: (product: Plan) => void;
}

const recentProductsStore = proxy<RecentProductsStoreState>({
  recentProducts: loadPersistedRecentProducts(),
  addRecentProduct: product => {
    const currentProducts = recentProductsStore.recentProducts;
    const existingProductIndex = currentProducts.findIndex(
      p => p.code === product.code
    );

    let updatedProducts = [...currentProducts];

    if (existingProductIndex > -1) {
      updatedProducts.splice(existingProductIndex, 1);
    }

    updatedProducts.unshift(product);

    if (updatedProducts.length > MAX_RECENT_PRODUCTS) {
      updatedProducts = updatedProducts.slice(0, MAX_RECENT_PRODUCTS);
    }

    recentProductsStore.recentProducts = updatedProducts;
  }
});

subscribe(
  recentProductsStore,
  () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(recentProductsStore.recentProducts)
      );
    } catch (error) {
      console.warn(`Error saving '${STORAGE_KEY}' to localStorage:`, error);
    }
  },
  true
);

export const getRecentProductsSnapshot = () => snapshot(recentProductsStore);
export const getRecentProductsStoreProxy = () => recentProductsStore;

export const useRecentProductsSnapshot = (
  options?: Parameters<typeof useSnapshot>[1]
) => useSnapshot(recentProductsStore, options);

export const useRecentProductsStore = (
  options?: Parameters<typeof useSnapshot>[1]
) => {
  return {
    recentProductsSnapshot: useSnapshot(recentProductsStore, options),
    recentProductsStore: recentProductsStore
  };
};
